import type { PropsWithChildren } from "react";
import React, { createContext, useEffect, useMemo } from "react";
import { Appearance, AppState, Platform } from "react-native";

import * as ExpoCar from "$modules/expo-car";
import type { Config } from "$src/core/models/config";
import type { Themes, Breakpoints } from "$src/core/models/theme";
import { isCarPlaySupported } from "$src/core/services/car/car";
import Logging from "$src/core/services/logging/logging";

type Props = {
  config: Config;
};

type ConfigContextType = {
  config: Config;
};

declare module "react-native-unistyles" {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface UnistylesThemes extends Themes {}
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  export interface UnistylesBreakpoints extends Breakpoints {}
}

export const ConfigContext = createContext<ConfigContextType | null>(null);

const ConfigProvider = ({ children, config }: PropsWithChildren<Props>) => {
  const value = useMemo(
    () => ({
      config,
    }),
    [config],
  );

  /* Force dark system UI; iOS goes via expo-car because `Appearance.setColorScheme` crashes on CarPlay's template scene. */
  useEffect(() => {
    if (Platform.OS === "web") return undefined;

    const apply = () => {
      if (isCarPlaySupported) {
        ExpoCar.setWindowInterfaceStyle("dark").catch((error: unknown) => {
          Logging.error("Could not apply dark interface style", { error });
        });
        return;
      }
      Appearance.setColorScheme("dark");
    };

    apply();
    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "active") apply();
    });
    return () => subscription.remove();
  }, []);

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  );
};

export default ConfigProvider;
